/**
 * PrestigeDimensionControls - Dimension controls specific to Prestige model
 * Uses stepper-style selectors with +/- buttons
 */
import { useEffect, useState } from 'react';
import { Typography } from 'antd';
import { PRESTIGE_DIMENSIONS } from '@infrastructure/three/prestigeService';
import type { UnitType } from '@ui/hooks/usePrestige';
import {
  formatDimensionCm,
  getPrestigeExternalDepthCm,
  getPrestigeExternalHeightCm,
  getPrestigeExternalWidthCm,
  parseDimensionInputToCm,
  findNearestStepByExternalCm,
} from '@domain/services/prestigeDimensionDisplay';

const { Text } = Typography;

const getRoomCount = (value: number): number => {
  return value < 39 ? 1 : 2;
};

const findStepIndex = (steps: readonly number[], value: number): number =>
  steps.findIndex((s) => Math.abs(s - value) < 1e-4);

/* ── Unit toggle sub-component ── */

const UNIT_OPTIONS: { value: UnitType; label: string }[] = [
  { value: 'cm', label: 'cm' },
  { value: 'inches', label: 'ft / in' },
];

const UnitToggle = ({
  unit,
  onChange,
}: {
  unit: UnitType;
  onChange: (u: UnitType) => void;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 4,
    }}
  >
    <div
      style={{
        display: 'inline-flex',
        borderRadius: 20,
        border: '1px solid #d9d9d9',
        overflow: 'hidden',
      }}
    >
      {UNIT_OPTIONS.map((opt) => {
        const isActive = unit === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              padding: '6px 20px',
              fontSize: 13,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? '#fff' : '#555',
              background: isActive ? '#555' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              lineHeight: 1.4,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const DimensionsIcon = ({ on }: { on: boolean }) => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke={on ? '#71903e' : '#aaa'}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7h18" />
    <path d="M3 17h18" />
    <path d="M7 4v6" />
    <path d="M17 14v6" />
    <path d="M3 12h18" strokeDasharray="2 2" />
  </svg>
);

const ToggleRow = ({
  active,
  onClick,
  label,
  description,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  description: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      gap: 14,
      padding: '14px 16px',
      border: active ? '2px solid #71903e' : '1px solid #d9d9d9',
      borderRadius: 10,
      background: active ? 'rgba(113,144,62,0.06)' : '#fff',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }}
  >
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 10,
        background: active ? 'rgba(140,167,255,0.14)' : '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <DimensionsIcon on={active} />
    </div>

    <div style={{ flex: 1, textAlign: 'left' }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
        {description}
      </div>
    </div>

    <div
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: active ? '#71903e' : '#ccc',
        position: 'relative',
        flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 3,
          left: active ? 23 : 3,
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  </button>
);

/* ── Engineered drawings download ── */

const DownloadIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 4v11" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

export const DownloadDrawingsButton = ({
  onDownload,
}: {
  onDownload: () => Promise<void>;
}) => {
  const [status, setStatus] = useState<'idle' | 'generating' | 'error'>('idle');

  const handleClick = async () => {
    setStatus('generating');
    try {
      await onDownload();
      setStatus('idle');
    } catch (error) {
      console.error('Failed to generate engineered drawings', error);
      setStatus('error');
    }
  };

  const generating = status === 'generating';

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        disabled={generating}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: '100%',
          height: 46,
          border: '1px solid #71903e',
          borderRadius: 8,
          background: generating ? 'rgba(113,144,62,0.06)' : '#fff',
          color: '#71903e',
          fontSize: 14,
          fontWeight: 600,
          cursor: generating ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <DownloadIcon />
        {generating ? 'Generating drawings…' : 'Download Engineered Drawings'}
      </button>
      {status === 'error' && (
        <Text type="danger" style={{ display: 'block', textAlign: 'center', marginTop: 4, fontSize: 12 }}>
          Couldn&rsquo;t generate the drawings. Please try again.
        </Text>
      )}
    </div>
  );
};

/* ── Stepper sub-component ── */

type StepperProps = {
  label: string;
  displayValue: string;
  rangeMin: string;
  rangeMax: string;
  onDecrement: () => void;
  onIncrement: () => void;
  onCommitInput: (raw: string) => void;
  canDecrement: boolean;
  canIncrement: boolean;
  subtitle?: string;
};

const stepperButton: React.CSSProperties = {
  width: 44,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontSize: 20,
  fontWeight: 500,
  color: '#333',
  flexShrink: 0,
  userSelect: 'none',
};

const DimensionStepper = ({
  label,
  displayValue,
  rangeMin,
  rangeMax,
  onDecrement,
  onIncrement,
  onCommitInput,
  canDecrement,
  canIncrement,
  subtitle,
}: StepperProps) => {
  // Local draft so typing isn't reformatted on every keystroke. It stays in
  // sync with the committed value whenever the field isn't being edited.
  const [draft, setDraft] = useState(displayValue);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(displayValue);
  }, [displayValue, editing]);

  const commit = () => {
    setEditing(false);
    onCommitInput(draft);
  };

  return (
  <div style={{ marginBottom: 4 }}>
    <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 14 }}>
      {label}
      {subtitle && (
        <Text type="secondary" style={{ fontWeight: 400, fontSize: 12, marginLeft: 6 }}>
          {subtitle}
        </Text>
      )}
    </Text>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        overflow: 'hidden',
        height: 46,
      }}
    >
      <button
        type="button"
        onClick={onDecrement}
        disabled={!canDecrement}
        style={{
          ...stepperButton,
          opacity: canDecrement ? 1 : 0.3,
          cursor: canDecrement ? 'pointer' : 'default',
          borderRight: '1px solid #d9d9d9',
        }}
        aria-label={`Decrease ${label}`}
      >
        &minus;
      </button>

      <input
        type="text"
        inputMode="decimal"
        value={draft}
        onFocus={(e) => {
          setEditing(true);
          setDraft(displayValue);
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Commit directly rather than relying on blur()'s side effect so
            // Enter always applies, then defocus for feedback.
            commit();
            e.currentTarget.blur();
          }
          if (e.key === 'Escape') {
            setEditing(false);
            setDraft(displayValue);
            e.currentTarget.blur();
          }
        }}
        aria-label={`${label} value`}
        style={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          textAlign: 'center',
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: 0.3,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: '#333',
          padding: 0,
        }}
      />

      <button
        type="button"
        onClick={onIncrement}
        disabled={!canIncrement}
        style={{
          ...stepperButton,
          opacity: canIncrement ? 1 : 0.3,
          cursor: canIncrement ? 'pointer' : 'default',
          borderLeft: '1px solid #d9d9d9',
        }}
        aria-label={`Increase ${label}`}
      >
        +
      </button>
    </div>

    <Text
      type="secondary"
      style={{ display: 'block', textAlign: 'center', marginTop: 4, fontSize: 12 }}
    >
      Range: {rangeMin} &ndash; {rangeMax}
    </Text>
  </div>
  );
};

/* ── Main component ── */

type Props = {
  widthValue: number;
  depthValue: number;
  heightValue: number;
  unit: UnitType;
  showDimensions: boolean;
  onWidthChange: (value: number) => void;
  onDepthChange: (value: number) => void;
  onHeightChange: (value: number) => void;
  onUnitChange: (value: UnitType) => void;
  onShowDimensionsChange: (enabled: boolean) => void;
  /** Present once the build has a number to put on the drawing. */
  onDownloadDrawings?: () => Promise<void>;
};

export const PrestigeDimensionControls = ({
  widthValue,
  depthValue,
  heightValue,
  unit,
  showDimensions,
  onWidthChange,
  onDepthChange,
  onHeightChange,
  onUnitChange,
  onShowDimensionsChange,
  onDownloadDrawings,
}: Props) => {
  const roomCount = getRoomCount(widthValue);

  const heightSteps = PRESTIGE_DIMENSIONS.height;
  const widthSteps = PRESTIGE_DIMENSIONS.width;
  const depthSteps = PRESTIGE_DIMENSIONS.depth;

  const heightIdx = findStepIndex(heightSteps, heightValue);
  const widthIdx = findStepIndex(widthSteps, widthValue);
  const depthIdx = findStepIndex(depthSteps, depthValue);

  // Parse a typed size and snap it to the nearest supported step.
  const commitWidthInput = (raw: string) => {
    const cm = parseDimensionInputToCm(raw, unit);
    if (cm === null) return;
    const nearest = findNearestStepByExternalCm(widthSteps, getPrestigeExternalWidthCm, cm);
    if (nearest !== null) onWidthChange(nearest);
  };

  const commitHeightInput = (raw: string) => {
    const cm = parseDimensionInputToCm(raw, unit);
    if (cm === null) return;
    const nearest = findNearestStepByExternalCm(heightSteps, getPrestigeExternalHeightCm, cm);
    if (nearest !== null) onHeightChange(nearest);
  };

  const commitDepthInput = (raw: string) => {
    const cm = parseDimensionInputToCm(raw, unit);
    if (cm === null) return;
    const nearest = findNearestStepByExternalCm(depthSteps, getPrestigeExternalDepthCm, cm);
    if (nearest !== null) onDepthChange(nearest);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 10 }}>
      <p
        style={{
          textAlign: 'center',
          fontSize: 15,
          color: '#444',
          margin: '0 0 4px',
        }}
      >
        Choose the size of your <strong>pergola</strong>:
      </p>

      <UnitToggle unit={unit} onChange={onUnitChange} />

      

      {/* Height */}
      <DimensionStepper
        label="Height"
        displayValue={formatDimensionCm(getPrestigeExternalHeightCm(heightValue), unit)}
        rangeMin={formatDimensionCm(getPrestigeExternalHeightCm(heightSteps[0]), unit)}
        rangeMax={formatDimensionCm(getPrestigeExternalHeightCm(heightSteps.at(-1)!), unit)}
        canDecrement={heightIdx > 0}
        canIncrement={heightIdx < heightSteps.length - 1}
        onDecrement={() => heightIdx > 0 && onHeightChange(heightSteps[heightIdx - 1])}
        onIncrement={() =>
          heightIdx < heightSteps.length - 1 && onHeightChange(heightSteps[heightIdx + 1])
        }
        onCommitInput={commitHeightInput}
      />

      {/* Width */}
      <DimensionStepper
        label="Width"
        displayValue={formatDimensionCm(getPrestigeExternalWidthCm(widthValue), unit)}
        subtitle={`(${roomCount} room${roomCount > 1 ? 's' : ''})`}
        rangeMin={formatDimensionCm(getPrestigeExternalWidthCm(widthSteps[0]), unit)}
        rangeMax={formatDimensionCm(getPrestigeExternalWidthCm(widthSteps.at(-1)!), unit)}
        canDecrement={widthIdx > 0}
        canIncrement={widthIdx < widthSteps.length - 1}
        onDecrement={() => widthIdx > 0 && onWidthChange(widthSteps[widthIdx - 1])}
        onIncrement={() =>
          widthIdx < widthSteps.length - 1 && onWidthChange(widthSteps[widthIdx + 1])
        }
        onCommitInput={commitWidthInput}
      />

      <DimensionStepper
        label="Depth"
        displayValue={formatDimensionCm(getPrestigeExternalDepthCm(depthValue), unit)}
        rangeMin={formatDimensionCm(getPrestigeExternalDepthCm(depthSteps[0]), unit)}
        rangeMax={formatDimensionCm(getPrestigeExternalDepthCm(depthSteps.at(-1)!), unit)}
        canDecrement={depthIdx > 0}
        canIncrement={depthIdx < depthSteps.length - 1}
        onDecrement={() => depthIdx > 0 && onDepthChange(depthSteps[depthIdx - 1])}
        onIncrement={() =>
          depthIdx < depthSteps.length - 1 && onDepthChange(depthSteps[depthIdx + 1])
        }
        onCommitInput={commitDepthInput}
      />
      <ToggleRow
        active={showDimensions}
        onClick={() => onShowDimensionsChange(!showDimensions)}
        label="Show dimensions"
        description={showDimensions ? 'Visible in 3D scene' : 'Hidden in 3D scene'}
      />
      {onDownloadDrawings && <DownloadDrawingsButton onDownload={onDownloadDrawings} />}
    </div>
  );
};

export default PrestigeDimensionControls;
